import { CsvButton } from "@/components/CsvButton";
import { downloadCsv, today } from "@/lib/csv";
import AtgOddsTable from "@/components/AtgOddsTable";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, RefreshCw, DollarSign, Target } from "lucide-react";

import { LinesSupport } from "./LinesSupport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  APPROVED: "bg-lime-500 text-slate-950",
  VALUE: "bg-amber-500/90 text-slate-950",
  WATCH: "bg-cyan-600 text-white",
  PASS: "bg-slate-800 text-slate-500",
  BLOCKED: "bg-rose-950 text-rose-300",
};
const RISK = { LOW: "text-lime-400", MEDIUM: "text-amber-400", HIGH: "text-rose-400" };

const fmtOdds = (o) => (o == null ? "—" : o > 0 ? `+${o}` : `${o}`);
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

const Pill = ({ s }) => (
  <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${STATUS[s] || "bg-slate-800 text-slate-400"}`}>{s}</span>
);

const BestCard = ({ icon, title, accent, pick, body }) => (
  <div className={`rounded-xl border p-3 ${accent}`} data-testid={`best-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="mb-1.5 flex items-center justify-between">
      <h3 className="flex items-center gap-1.5 font-display text-[12px] font-bold uppercase tracking-wide">{icon} {title}</h3>
      {pick && <Pill s={pick.status} />}
    </div>
    {pick ? body(pick) : <p className="py-4 text-center font-mono text-[10px] uppercase tracking-widest text-slate-600">No qualifying value</p>}
  </div>
);

const Kv = ({ k, v, tone = "text-slate-200" }) => (
  <div className="flex items-center justify-between font-mono text-[11px]">
    <span className="text-slate-500">{k}</span>
    <span className={`font-bold ${tone}`}>{v}</span>
  </div>
);

export default function GameLinesTab({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(false);
    try {
      const r = await axios.get(`${API}/game-lines`, { params: apiDate ? { date: apiDate } : {}, timeout: 240000 });
      setData(r.data);
    } catch (e) { console.error(e); setErr(true); }
    finally { setLoading(false); }
  }, [apiDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="gl-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Building lines model…</p>
    </div>
  );
  if (err || !data) return (
    <div className="flex flex-col items-center justify-center py-32" data-testid="gl-error">
      <p className="font-mono text-xs uppercase tracking-widest text-rose-400">Game lines unavailable</p>
      <button onClick={load} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
    </div>
  );
  if (!data.games.length) return (
    <div className="py-24 text-center" data-testid="gl-empty">
      <p className="font-mono text-xs uppercase tracking-widest text-slate-500">No games scheduled / no odds posted</p>
    </div>
  );

  const b = data.best;
  return (
    <div className="space-y-4" data-testid="game-lines-dashboard">
      <AtgOddsTable apiDate={apiDate} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-cyan-300">🧊 NHL Game Lines Model</h2>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {data.slate_size} games · lg {data.league_gf} GF/GP
          </p>
        </div>
        <div className="flex items-center gap-2">
        <CsvButton label="Lines CSV" testId="csv-game-lines" onClick={() => downloadCsv(`game-lines_${today()}.csv`,
          data.games.flatMap((g) => ["moneyline", "puckline", "total"].map((mk) => {
            const m = g.markets?.[mk] || {};
            return {
              game: `${g.away} @ ${g.home}`, date: g.date, status: g.status_detail, market: mk, pick: m.label || m.selection,
              line: m.line ?? m.sel_spread, odds: m.odds, fair: m.fair, model_pct: m.model_pct ?? m.cover_pct, raw_model_pct: m.raw_model_pct, novig_pct: m.novig_pct, raw_implied_pct: m.raw_implied_pct, decimal: m.decimal, edge: m.edge, ev: m.ev, exp_roi: m.exp_roi,
              rating: m.status, risk: m.risk, best_bet: g.best_bet?.market === mk ? "YES" : "",
              exp_away: g.exp_away, exp_home: g.exp_home, model_total: g.model_total,
              goalie_away: g.goalie_away?.name || g.goalie_away, goalie_home: g.goalie_home?.name || g.goalie_home,
            };
          })))} />
        <button onClick={load} data-testid="gl-refresh" className="flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-cyan-300 hover:bg-cyan-950/60">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
        </div>
      </div>

      {/* best bet cards */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <BestCard icon={<DollarSign className="h-4 w-4 text-lime-400" />} title="Best Moneyline" accent="border-lime-700/50 bg-lime-950/15" pick={b.moneyline}
          body={(p) => (<div className="space-y-1">
            <p className="font-display text-sm font-bold text-slate-100">{p.game} · <span className="text-lime-300">{p.pick_team} ML</span></p>
            <Kv k="Market" v={fmtOdds(p.odds)} /><Kv k="Model %" v={`${p.model_pct}%`} tone="text-lime-300" />
            <Kv k="Fair" v={fmtOdds(p.fair)} /><Kv k="Edge" v={`${Number(p.edge).toFixed(1)} pts`} tone="text-lime-300" />
            <Kv k="Exp ROI" v={`${p.exp_roi}%`} /><Kv k="Support" v={p.support ?? "—"} /><Kv k="Risk" v={p.risk} tone={RISK[p.risk]} /></div>)} />
        <BestCard icon="🏒" title="Best Puck Line" accent="border-cyan-700/50 bg-cyan-950/15" pick={b.puckline}
          body={(p) => (<div className="space-y-1">
            <p className="font-display text-sm font-bold text-slate-100">{p.game} · <span className="text-cyan-300">{p.pick_team} {p.sel_spread > 0 ? `+${p.sel_spread}` : p.sel_spread}</span></p>
            <Kv k="Market" v={fmtOdds(p.odds)} /><Kv k="Cover %" v={`${p.cover_pct}%`} tone="text-cyan-300" />
            <Kv k="Fair" v={fmtOdds(p.fair)} /><Kv k="Edge" v={`${Number(p.edge).toFixed(1)} pts`} tone="text-cyan-300" />
            <Kv k="Exp ROI" v={`${p.exp_roi}%`} /><Kv k="Support" v={p.support ?? "—"} /><Kv k="Risk" v={p.risk} tone={RISK[p.risk]} /></div>)} />
        <BestCard icon={<Target className="h-4 w-4 text-amber-400" />} title="Best Game Total" accent="border-amber-700/50 bg-amber-950/15" pick={b.total}
          body={(p) => (<div className="space-y-1">
            <p className="font-display text-sm font-bold text-slate-100">{p.game} · <span className="text-amber-300">{p.selection} {p.line}</span></p>
            <Kv k="Market" v={fmtOdds(p.odds)} /><Kv k="Model Total" v={p.model_total} tone="text-amber-300" />
            <Kv k="O/U %" v={`${p.model_pct}%`} /><Kv k="Fair" v={fmtOdds(p.fair)} />
            <Kv k="Edge" v={`${Number(p.edge).toFixed(1)} pts`} tone="text-amber-300" /><Kv k="Exp ROI" v={`${p.exp_roi}%`} /><Kv k="Support" v={p.support ?? "—"} /><Kv k="Risk" v={p.risk} tone={RISK[p.risk]} /></div>)} />
      </div>

      {/* best bet for EACH game */}
      <div>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">🎯 Best Bet · Each Game</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="gl-best-per-game">
          {data.games.map((g) => {
            const bb = g.best_bet;
            return (
              <div key={`bb-${g.event_id}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5" data-testid={`gl-bestbet-${g.away}-${g.home}`}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-mono text-[11px] font-bold text-slate-200">{g.away} @ {g.home}</span>
                  <Pill s={bb ? bb.status : g.status} />
                </div>
                {bb ? (
                  <div className="flex items-center justify-between font-mono text-[11px]">
                    <span className="font-bold text-cyan-300">{bb.label} <span className="text-slate-500">({bb.market === "moneyline" ? "ML" : bb.market === "puckline" ? "PL" : "TOT"})</span></span>
                    <span className="flex items-center gap-2">
                      <span className="text-slate-400">{fmtOdds(bb.odds)}</span>
                      <span className={`font-bold ${bb.edge > 0 ? "text-lime-400" : "text-slate-600"}`}>{bb.edge}%</span>
                      <span className="text-[9px] text-slate-400">EV {bb.exp_roi}%</span>
                      <span className={`text-[9px] font-bold ${RISK[bb.risk]}`}>{bb.risk}</span>
                    </span>
                  </div>
                ) : <span className="font-mono text-[10px] text-slate-500" data-testid={`gl-noqual-${g.away}-${g.home}`}>{g.best_bet_note || g.note || "No odds"}</span>}
              </div>
            );
          })}
        </div>
      </div>

      <LinesSupport games={data.games} apiDate={apiDate} />

      {/* game lines table */}
      <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="gl-table">
        <table className="w-full min-w-[920px] border-collapse">
          <thead>
            <tr className="bg-slate-900/80 font-mono text-[9px] uppercase tracking-wider text-slate-500">
              {["Status", "Game", "Time", "Away ML", "Home ML", "PL", "Total", "Mdl A%", "Mdl H%", "Mdl Tot", "ML Edge", "PL Edge", "Tot Edge", "Goalie", "Risk"].map((h) => <th key={h} className="whitespace-nowrap px-2 py-2 text-left">{h}</th>)}
            </tr>
          </thead>
          <tbody className="font-mono text-[11px]">
            {data.games.map((g) => {
              const m = g.markets || {};
              const ml = m.moneyline, pl = m.puckline, tot = m.total;
              return (
                <tr key={g.event_id} className="border-t border-slate-800/60" data-testid={`gl-row-${g.away}-${g.home}`}>
                  <td className="px-2 py-2"><Pill s={g.status} /></td>
                  <td className="whitespace-nowrap px-2 py-2 font-bold text-slate-200">{g.away} @ {g.home}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-slate-500">{fmtTime(g.date)}</td>
                  {ml ? (<>
                    <td className="px-2 py-2 text-slate-300">{fmtOdds(ml.away.odds)}</td>
                    <td className="px-2 py-2 text-slate-300">{fmtOdds(ml.home.odds)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-slate-400">±{pl.spread} {fmtOdds(pl.home.odds)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-slate-400">{tot.line} {fmtOdds(tot.over.odds)}/{fmtOdds(tot.under.odds)}</td>
                    <td className="px-2 py-2 text-slate-300">{ml.away.model_pct}%</td>
                    <td className="px-2 py-2 text-slate-300">{ml.home.model_pct}%</td>
                    <td className="px-2 py-2 font-bold text-amber-300">{g.model_total}</td>
                    <td className={`px-2 py-2 font-bold ${ml.edge > 0 ? "text-lime-400" : "text-slate-600"}`}>{ml.selection[0]} {ml.edge}% <span className="text-[9px] font-normal text-slate-500">EV {ml.exp_roi}%</span></td>
                    <td className={`px-2 py-2 font-bold ${pl.edge > 0 ? "text-cyan-400" : "text-slate-600"}`}>{pl.selection[0]} {pl.edge}% <span className="text-[9px] font-normal text-slate-500">EV {pl.exp_roi}%</span></td>
                    <td className={`px-2 py-2 font-bold ${tot.edge > 0 ? "text-amber-400" : "text-slate-600"}`}>{tot.selection[0]} {tot.edge}% <span className="text-[9px] font-normal text-slate-500">EV {tot.exp_roi}%</span></td>
                  </>) : (<td colSpan={10} className="px-2 py-2 text-rose-400">{g.note || "Data error"}</td>)}
                  <td className="whitespace-nowrap px-2 py-2 text-[10px]">{g.goalie_confirmed ? <span className="text-lime-400">✓</span> : <span className="text-amber-400">PROV</span>}</td>
                  <td className={`px-2 py-2 font-bold ${RISK[g.risk] || "text-slate-500"}`}>{g.risk}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-[9px] uppercase tracking-widest text-slate-600">
        Only 🟢 APPROVED = official pick (edge ≥ +3 pts AND expected ROI ≥ +3% at the actual price). 🟡 VALUE = edge ≥ +2 AND ROI ≥ +2%. 🔵 WATCH = goalie pending, HIGH uncertainty, or positive but below VALUE. 🔴 PASS = evaluated, edge/EV not positive. ⚫ BLOCKED = data missing. Model % is pulled toward the no-vig market (lines-v2.3: half pull + trends/H2H/form support — APPROVED also needs support ≥ 55, VALUE with support ≥ 65 is upgraded, VALUE with support ≤ 45 drops to WATCH; thresholds are experimental).
      </p>
    </div>
  );
}
